import { z } from 'zod';
import { CrmManagerEventInterestsSummaryDto } from './crm-contact';

/** Этап воронки v1; позже — настраиваемый справочник в админке. */
export const CrmDealStage = z.enum(['new', 'lead', 'negotiation', 'waiting', 'won', 'partner']);
export type CrmDealStage = z.infer<typeof CrmDealStage>;

export const CrmDealProductKind = z.enum(['pro', 'partner', 'investor', 'event', 'service', 'other']);
export type CrmDealProductKind = z.infer<typeof CrmDealProductKind>;

/** Быстрый фильтр доски (табы). */
export const CrmBoardSegment = z.enum(['all', 'organizers', 'participants', 'warm']);
export type CrmBoardSegment = z.infer<typeof CrmBoardSegment>;

/** Статус сотрудничества (вычисляется на чтении, не колонка БД). */
export const CrmPartnershipStatus = z.enum(['PARTNER', 'NEGOTIATING', 'EXTERNAL']);
export type CrmPartnershipStatus = z.infer<typeof CrmPartnershipStatus>;

export const CrmBoardQuery = z.object({
  segment: CrmBoardSegment.default('all'),
  cityId: z.uuid().optional(),
  partnershipStatus: CrmPartnershipStatus.optional(),
  /** Карточек на колонку в первом ответе; 0 = без лимита (внутренние вызовы). */
  stageLimit: z.coerce.number().int().min(0).max(100).optional(),
});
export type CrmBoardQuery = z.infer<typeof CrmBoardQuery>;

export const CrmBoardStageDealsQuery = CrmBoardQuery.extend({
  stage: CrmDealStage,
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type CrmBoardStageDealsQuery = z.infer<typeof CrmBoardStageDealsQuery>;

/**
 * Вычисляемый тип контакта для сегментов (не путать с CrmDealStage).
 * `lead` — остальные без organizer/participant признаков.
 */
export const CrmContactKind = z.enum(['organizer', 'participant', 'lead']);
export type CrmContactKind = z.infer<typeof CrmContactKind>;

export const CrmInboundChannel = z.enum(['upme', 'instagram_manychat', 'manual']);
export type CrmInboundChannel = z.infer<typeof CrmInboundChannel>;

export const CrmDealContactDto = z.object({
  userId: z.uuid(),
  /** upme — зарегистрированный User; instagram_manychat — CrmManyChatContact без Telegram. */
  inboundChannel: CrmInboundChannel.default('upme'),
  /** Id CrmManyChatContact, если inboundChannel=instagram_manychat (совпадает с userId в DTO). */
  manyChatContactId: z.uuid().optional(),
  /** Id CrmManualLead, если inboundChannel=manual (совпадает с userId в DTO). */
  manualLeadId: z.uuid().optional(),
  instagramUsername: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  leadNote: z.string().nullable().optional(),
  lastInquiryAt: z.iso.datetime().nullable().optional(),
  username: z.string().nullable(),
  contactLabel: z.string().min(1).max(120),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** URL аватара; null — показываем инициалы. */
  photoUrl: z.string().nullable(),
  cityId: z.uuid().nullable(),
  profileCompletenessPercent: z.number().int().min(0).max(100),
  /** Socionics (НТС) пройден — `NetworkingProfile.socionicsType` заполнен. */
  psychTestComplete: z.boolean(),
  friendsCount: z.number().int().nonnegative(),
  /** Прошедшие CONFIRMED EVENT|MEETING (как ConnectionCardStats). */
  meetingsCount: z.number().int().nonnegative(),
  /**
   * Прошлые опубликованные EVENT организатора с хотя бы одним CHECKED_IN
   * или групповым фото с тегами. Для «Active» организатора.
   */
  organizedEventsWithVerifiedAttendance: z.number().int().nonnegative(),
  botStartedAt: z.iso.datetime().nullable(),
  botBlockedAt: z.iso.datetime().nullable(),
  /** Заметки менеджера об интересах к мероприятиям (не пользовательский ProfileFacet). */
  managerEventInterestsSummary: CrmManagerEventInterestsSummaryDto,
});
export type CrmDealContactDto = z.infer<typeof CrmDealContactDto>;

export const CrmDealPartnerEconomicsSource = z.enum(['event', 'override', 'mixed']);
export type CrmDealPartnerEconomicsSource = z.infer<typeof CrmDealPartnerEconomicsSource>;

export const CrmDealPartnerEconomicsDto = z.object({
  slotsCount: z.number().int().positive(),
  commissionPercent: z.number().int().min(0).max(100),
  ticketPriceMinor: z.number().int().nonnegative(),
  currency: z.string().length(3),
  bountyPerSeatMinor: z.number().int().nonnegative(),
  poolMinor: z.number().int().nonnegative(),
  source: CrmDealPartnerEconomicsSource,
});
export type CrmDealPartnerEconomicsDto = z.infer<typeof CrmDealPartnerEconomicsDto>;

/**
 * Сделка CRM: этап относится к сделке, не ко всему контакту.
 * Заявки (MembershipLead, promotion и т.д.) бэкенд позже мапит в сделки — отдельного kind нет.
 */
export const CrmDealDto = z.object({
  id: z.uuid(),
  stage: CrmDealStage,
  contactUserId: z.uuid(),
  /** Подпись контакта (legacy + UI); дублирует contact.contactLabel. */
  contactLabel: z.string().min(1).max(120),
  contact: CrmDealContactDto,
  contactKind: CrmContactKind,
  partnershipStatus: CrmPartnershipStatus,
  title: z.string().min(1).max(200),
  productKind: CrmDealProductKind,
  amountMinor: z.number().int().nonnegative().nullable(),
  currency: z.string().length(3).nullable(),
  nextContactAt: z.iso.datetime().nullable(),
  /** % комиссии организатора; без него economics не считается. */
  commissionPercent: z.number().int().min(0).max(100).nullable(),
  partnerSlotsOverride: z.number().int().positive().nullable(),
  ticketPriceOverrideMinor: z.number().int().nonnegative().nullable(),
  /** null — нет данных или не organizer / нет commission. */
  partnerEconomics: CrmDealPartnerEconomicsDto.nullable(),
  /** Стабильный ключ карточки (overlay в CrmDeal.sourceKey). */
  sourceKey: z.string().min(1).max(64),
  eventId: z.uuid().nullable(),
  eventStartsAt: z.iso.datetime().nullable(),
  botStartedAt: z.iso.datetime().nullable(),
});
export type CrmDealDto = z.infer<typeof CrmDealDto>;

export const CrmBoardSegmentCounts = z.object({
  all: z.number().int().nonnegative(),
  organizers: z.number().int().nonnegative(),
  participants: z.number().int().nonnegative(),
  warm: z.number().int().nonnegative(),
});
export type CrmBoardSegmentCounts = z.infer<typeof CrmBoardSegmentCounts>;

export const CrmBoardStageCounts = z.object({
  new: z.number().int().nonnegative(),
  lead: z.number().int().nonnegative(),
  negotiation: z.number().int().nonnegative(),
  waiting: z.number().int().nonnegative(),
  won: z.number().int().nonnegative(),
  partner: z.number().int().nonnegative(),
});
export type CrmBoardStageCounts = z.infer<typeof CrmBoardStageCounts>;

/** Ответ `GET /admin/crm/board`. */
export const CrmBoardDto = z.object({
  deals: z.array(CrmDealDto),
  segment: CrmBoardSegment,
  countsBySegment: CrmBoardSegmentCounts,
  countsByStage: CrmBoardStageCounts,
});
export type CrmBoardDto = z.infer<typeof CrmBoardDto>;

export const CrmBoardStageDealsDto = z.object({
  stage: CrmDealStage,
  total: z.number().int().nonnegative(),
  deals: z.array(CrmDealDto),
});
export type CrmBoardStageDealsDto = z.infer<typeof CrmBoardStageDealsDto>;

/** Тело `PATCH /admin/crm/deals/:id/stage`. */
export const CrmMoveDealStageBody = z.object({
  stage: CrmDealStage,
  /** Для upsert overlay, если строки CrmDeal ещё нет (виртуальный id с доски). */
  sourceKey: z.string().min(1).max(64).optional(),
});
export type CrmMoveDealStageBody = z.infer<typeof CrmMoveDealStageBody>;

/** Этапы сегмента warm: новички / в обработке. */
export const CRM_WARM_STAGES: readonly CrmDealStage[] = ['new', 'lead', 'negotiation'];
